import React, { useState } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  IStringHash 
} from "@models";
import { 
  AbilityScores,
  Character,
} from "@dndModels";
// import { 
//   POINTS_FOR_NUM_MAP 
// } from '../../../models/Constants';

import CommonUtilities from "@utilities/common_utilities";

import StringBox from "@components/input/strings/StringBox"; 
import SelectStringBox from "@components/input/selects/SelectStringBox"; 

interface Props {
  character: Character;
  onChange: (changed: Character) => void;
}

const common_util = CommonUtilities.getInstance();

const calcPoints = (base_ability_scores: AbilityScores, options_points: number, POINTS_FOR_NUM_MAP: { [ability_score: number]: number }) => {
  let points = options_points;
  points -= POINTS_FOR_NUM_MAP[base_ability_scores.strength];
  points -= POINTS_FOR_NUM_MAP[base_ability_scores.dexterity];
  points -= POINTS_FOR_NUM_MAP[base_ability_scores.constitution];
  points -= POINTS_FOR_NUM_MAP[base_ability_scores.intelligence];
  points -= POINTS_FOR_NUM_MAP[base_ability_scores.wisdom];
  points -= POINTS_FOR_NUM_MAP[base_ability_scores.charisma];
  return points;
}

const calcRemaining = (base_ability_scores: AbilityScores, options_standard_array: number[]) => {
  const remaining = [...options_standard_array];
  let pos = 0;
  const values: number[] = [];
  if (base_ability_scores.strength !== 0) {
    values.push(base_ability_scores.strength);
  }
  if (base_ability_scores.dexterity !== 0) {
    values.push(base_ability_scores.dexterity);
  }
  if (base_ability_scores.constitution !== 0) {
    values.push(base_ability_scores.constitution);
  }
  if (base_ability_scores.intelligence !== 0) {
    values.push(base_ability_scores.intelligence);
  }
  if (base_ability_scores.wisdom !== 0) {
    values.push(base_ability_scores.wisdom);
  }
  if (base_ability_scores.charisma !== 0) {
    values.push(base_ability_scores.charisma);
  }
  while (pos < remaining.length && values.length > 0) {
    const value = remaining[pos];
    if (values.includes(remaining[pos])) {
      remaining.splice(pos, 1);
      let pos2 = 0;
      while (pos2 < values.length) {
        if (values[pos2] === value) {
          values.splice(pos2, 1);
          break;
        } else {
          pos2++;
        }
      }
    } else {
      pos++;
    }
  }
  return remaining;
}

export default function CharacterAbilityScoresInput({
  character,
  onChange
}: Props) {
  const [mode, setMode] = useState(character.ability_score_gen_method);
  const [points, setPoints] = useState(calcPoints(character.base_ability_scores, character.character_options.points, character.character_options.point_mapping));
  const [remaining_standard_array, setRemainingStandardArray] = useState(calcRemaining(character.base_ability_scores, character.character_options.standard_array));
  // const [display_calcs, setDisplayCalcs] = useState("");
  const [base_ability_scores, setBaseAbilityScores] = useState(new AbilityScores(character.base_ability_scores));
  const [current_ability_scores, setCurrentAbilityScores] = useState(new AbilityScores(character.current_ability_scores));
  
  const renderRemainingStandardArray = () => {
    return (
      <Grid>Options Remaining: 
                { remaining_standard_array.map((num, key) => {
          return (
            <span key={key}>{ key > 0 && ", " }{ num }</span>
          );
        }) }
      </Grid>
    );
  }

  const displayModifier = (modifier: number) => {
    return common_util.addPlusMaybe(modifier);
  }

  const renderOption = () => {
    if (mode === "Point Buy") {
      return (
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 12
          }}>
          { renderPointBuySelect("Strength") }
          { renderPointBuySelect("Dexterity") }
          { renderPointBuySelect("Constitution") }
          { renderPointBuySelect("Intelligence") }
          { renderPointBuySelect("Wisdom") }
          { renderPointBuySelect("Charisma") }
        </Grid>
      );
    } else if (mode === "Standard Array") {
      return (
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 12
          }}>
          { renderStandardArraySelect("Strength") }
          { renderStandardArraySelect("Dexterity") }
          { renderStandardArraySelect("Constitution") }
          { renderStandardArraySelect("Intelligence") }
          { renderStandardArraySelect("Wisdom") }
          { renderStandardArraySelect("Charisma") }
        </Grid>
      );
    } else if (mode === "Manual/Rolled") {
      return (
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 12
          }}>
          { renderManual("Strength") }
          { renderManual("Dexterity") }
          { renderManual("Constitution") }
          { renderManual("Intelligence") }
          { renderManual("Wisdom") }
          { renderManual("Charisma") }
        </Grid>
      );
    }
    return (
      <Grid
        size={{
          xs: 12
        }}>Not Implemented</Grid>
    );
  }

  const renderPointBuySelect = (ability: string) => {
    const AS_point_map: IStringHash = {};
    const options: string[] = [];

    let ability_score = 0;
    switch(ability) {
      case "Strength":
        ability_score = base_ability_scores.strength;
      break;
      case "Dexterity":
        ability_score = base_ability_scores.dexterity;
      break;
      case "Constitution":
        ability_score = base_ability_scores.constitution;
      break;
      case "Intelligence":
        ability_score = base_ability_scores.intelligence;
      break;
      case "Wisdom":
        ability_score = base_ability_scores.wisdom;
      break;
      case "Charisma":
        ability_score = base_ability_scores.charisma;
      break;
    }
    for (let i = 8; i < 16; i++) {
      if (i < ability_score) {
        options.push(`${i}`);
        AS_point_map[`${i}`] = `${i} (+${character.character_options.point_mapping[ability_score] - character.character_options.point_mapping[i]} Points)`;
      } else if (i > ability_score) {
        const diff = character.character_options.point_mapping[i] - character.character_options.point_mapping[ability_score];
        if (diff <= points) {
          options.push(`${i}`);
          AS_point_map[`${i}`] = `${i} (-${diff} Points)`;
        }
      } else {
        options.push(`${i}`);
        AS_point_map[`${i}`] = `${i}`;
      }
    }

    return (
      <Grid
        size={{
          xs: 4
        }}>
        <SelectStringBox
          name={ability}
          options={options}
          display_map={AS_point_map}
          value={`${ability_score}`}
          onChange={(value: number) => {
            const new_base_ability_scores = base_ability_scores;
            const new_current_ability_scores = current_ability_scores;
            new_current_ability_scores.minus(new_base_ability_scores);
            new_base_ability_scores.setAbilityScore(ability, +value);
            new_current_ability_scores.add(new_base_ability_scores);
            setBaseAbilityScores(new_base_ability_scores);
            setCurrentAbilityScores(new_current_ability_scores);
            setPoints(calcPoints(new_base_ability_scores, character.character_options.points, character.character_options.point_mapping));
            const obj = character;
            obj.base_ability_scores = new_base_ability_scores.clone();
            onChange(obj);
          }}
        />
      </Grid>
    );
  }

  const renderStandardArraySelect = (ability: string) => {
    let ability_score = 0;
    switch(ability) {
      case "Strength":
        ability_score = base_ability_scores.strength;
      break;
      case "Dexterity":
        ability_score = base_ability_scores.dexterity;
      break;
      case "Constitution":
        ability_score = base_ability_scores.constitution;
      break;
      case "Intelligence":
        ability_score = base_ability_scores.intelligence;
      break;
      case "Wisdom":
        ability_score = base_ability_scores.wisdom;
      break;
      case "Charisma":
        ability_score = base_ability_scores.charisma;
      break;
    }

    let options: number[] = [];
    if (ability_score === 0) {
      options = remaining_standard_array;
    } else {
      options = [ability_score, ...remaining_standard_array].sort();
    } 

    return (
      <Grid
        size={{
          xs: 4
        }}>
        <SelectStringBox
          name={ability}
          options={ ["Select an Option", ...options.map(o => `${o}`)] }
          value={ ability_score === 0 ? "Select an Option" : `${ability_score}` }
          onChange={(value: string) => {
            const num_value = value === "Select an Option" ? 0 : +value;
            const new_base_ability_scores = base_ability_scores;
            const new_current_ability_scores = current_ability_scores;
            new_current_ability_scores.minus(new_base_ability_scores);
            new_base_ability_scores.setAbilityScore(ability, num_value);
            new_current_ability_scores.add(new_base_ability_scores);
            setBaseAbilityScores(new_base_ability_scores);
            setCurrentAbilityScores(new_current_ability_scores);
            setRemainingStandardArray(calcRemaining(new_base_ability_scores, character.character_options.standard_array)); 
            const obj = character;
            obj.base_ability_scores = new_base_ability_scores.clone();
            onChange(obj);
          }}
        />
      </Grid>
    );
  }

  const renderManual = (ability: string) => {
    let ability_score = 0;
    switch(ability) {
      case "Strength":
        ability_score = base_ability_scores.strength;
      break;
      case "Dexterity":
        ability_score = base_ability_scores.dexterity;
      break;
      case "Constitution":
        ability_score = base_ability_scores.constitution;
      break;
      case "Intelligence":
        ability_score = base_ability_scores.intelligence;
      break;
      case "Wisdom":
        ability_score = base_ability_scores.wisdom;
      break;
      case "Charisma":
        ability_score = base_ability_scores.charisma;
      break;
    }

    return (
      <Grid
        size={{
          xs: 4
        }}>
        <StringBox
          name={ability}
          value={`${ability_score}`}
          type="number"
          onBlur={(value: string) => {
            const new_base_ability_scores = base_ability_scores;
            const new_current_ability_scores = current_ability_scores;
            new_current_ability_scores.minus(new_base_ability_scores);
            new_base_ability_scores.setAbilityScore(ability, +value);
            new_current_ability_scores.add(new_base_ability_scores);
            setBaseAbilityScores(new_base_ability_scores);
            setCurrentAbilityScores(new_current_ability_scores);
            const obj = character;
            obj.base_ability_scores = new AbilityScores(base_ability_scores);
            onChange(obj);
          }}
        />
      </Grid>
    );
  }
  
  const mode_options = (character.character_options.ability_score_gen_method === "Any" ? 
    ["Point Buy","Standard Array","Manual/Rolled"] : 
    (character.character_options.ability_score_gen_method === "Not Manual/Rolled" ? 
      ["Point Buy","Standard Array"] : [character.character_options.ability_score_gen_method]
    )
  );

  const renderAbilityScoreMaxes = () => {
    const max_ability_scores = character.max_ability_scores;
    const max = Math.max(max_ability_scores.strength, max_ability_scores.dexterity, max_ability_scores.constitution, max_ability_scores.intelligence, max_ability_scores.wisdom, max_ability_scores.charisma);
    if (max !== 20) {
      return (
        <>
          <Grid
            className="header centered"
            size={{
              xs: 12
            }}>
            Ability Score Maximums
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            Strength: { max_ability_scores.strength } ({ displayModifier(max_ability_scores.STR) })
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            Dexterity: { max_ability_scores.dexterity } ({ displayModifier(max_ability_scores.DEX) })
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            Constitution: { max_ability_scores.constitution } ({ displayModifier(max_ability_scores.CON) })
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            Intelligence: { max_ability_scores.intelligence } ({ displayModifier(max_ability_scores.INT) })
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            Wisdom: { max_ability_scores.wisdom } ({ displayModifier(max_ability_scores.WIS) })
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            Charisma: { max_ability_scores.charisma } ({ displayModifier(max_ability_scores.CHA) })
          </Grid>
        </>
      );
    }
    return null;
  };

  return (
    <Grid container spacing={0} direction="row">
      <Grid
        className="header centered"
        size={{
          xs: 12
        }}>
        Ability Scores
      </Grid>
      <Grid
        size={{
          xs: 12
        }}>
        { mode_options.length > 0 ?
          <SelectStringBox 
            name="Choose a Generation Method"
            options={mode_options}
            value={mode} 
            onChange={(value: string) => {
              let changed: "Point Buy" | "Standard Array" | "Manual/Rolled" = "Point Buy";
              switch(value) {
                case "Standard Array": changed = "Standard Array"; break;
                case "Manual/Rolled": changed = "Manual/Rolled"; break;
              }
              let new_base_ability_scores = base_ability_scores;
              if (changed === "Point Buy") {
                new_base_ability_scores = new AbilityScores();
              } else {
                new_base_ability_scores = new AbilityScores(0);
              }
              const obj = character;
              const new_current_ability_scores = current_ability_scores;
              new_current_ability_scores.minus(obj.base_ability_scores);
              new_current_ability_scores.plus(new_base_ability_scores);
              setBaseAbilityScores(new_base_ability_scores);
              setCurrentAbilityScores(new_current_ability_scores);
              setPoints(character.character_options.points);
              setRemainingStandardArray([...character.character_options.standard_array]);
              setMode(changed);
              obj.ability_score_gen_method = changed;
              obj.base_ability_scores = new_base_ability_scores;
              onChange(obj);
            }}
          />
        : 
          <span>{ character.character_options.ability_score_gen_method }</span>
        }
      </Grid>
      { mode === "Point Buy" ?
        <>
          <Grid
            size={{
              xs: 12
            }}>
            Points Remaining: { points } / { character.character_options.points }
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            { renderOption() }
          </Grid>
        </>
      : mode === "Standard Array" ?
        <>
          { renderRemainingStandardArray() }
          <Grid
            size={{
              xs: 12
            }}>
            { renderOption() }
          </Grid>
        </>
      :
        <Grid
          size={{
            xs: 12
          }}>
          { renderOption() }
        </Grid>
      }
      <Grid
        className="header centered"
        size={{
          xs: 12
        }}>
        Current Ability Scores
      </Grid>
      <Grid
        onClick={() => {
          // setDisplayCalcs((display_calcs === "Strength" ? "" : "Strength"));
        }}
        size={{
          xs: 4
        }}>
        Strength: { current_ability_scores.strength } ({ displayModifier(current_ability_scores.STR) })
      </Grid>
      <Grid
        onClick={() => {
          // setDisplayCalcs((display_calcs === "Dexterity" ? "" : "Dexterity"));
        }}
        size={{
          xs: 4
        }}>
        Dexterity: { current_ability_scores.dexterity } ({ displayModifier(current_ability_scores.DEX) })
      </Grid>
      <Grid
        onClick={() => {
          // setDisplayCalcs((display_calcs === "Constitution" ? "" : "Constitution"));
        }}
        size={{
          xs: 4
        }}>
        Constitution: { current_ability_scores.constitution } ({ displayModifier(current_ability_scores.CON) })
      </Grid>
      <Grid
        onClick={() => {
          // setDisplayCalcs((display_calcs === "Intelligence" ? "" : "Intelligence"));
        }}
        size={{
          xs: 4
        }}>
        Intelligence: { current_ability_scores.intelligence } ({ displayModifier(current_ability_scores.INT) })
      </Grid>
      <Grid
        onClick={() => {
          // setDisplayCalcs((display_calcs === "Wisdom" ? "" : "Wisdom"));
        }}
        size={{
          xs: 4
        }}>
        Wisdom: { current_ability_scores.wisdom } ({ displayModifier(current_ability_scores.WIS) })
      </Grid>
      <Grid
        onClick={() => {
          // setDisplayCalcs((display_calcs === "Charisma" ? "" : "Charisma"));
        }}
        size={{
          xs: 4
        }}>
        Charisma: { current_ability_scores.charisma } ({ displayModifier(current_ability_scores.CHA) })
      </Grid>
      {/* { display_calcs !== "" && 
        <Grid item xs={12}>
          { display_calcs } Calculations
        </Grid>
      } */}
      { renderAbilityScoreMaxes() }
    </Grid>
  );
}
